import { type FormEvent } from "react";
import { FileSpreadsheetIcon, Loader2, XIcon } from "lucide-react";

import { FormCheckbox, FormCheckboxGroup } from "@/components/forms/FormCheckbox";
import { FormInput } from "@/components/forms/FormInput";
import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldGroup } from "@/components/ui/field";
import { ListCart, type ListCartProps } from "./ListCart";

export type ListSettingsFormProps = {
    onSubmit: (e: FormEvent<HTMLFormElement>) => void;
    sheetExportError: string | null;
    onDismissExportError: () => void;
    isCreatorRequestClosed: boolean;
    canExportList: boolean;
    isExportPending: boolean;
    onExport: () => void;
    name: string;
    isNameDisabled: boolean;
    onNameChange: (value: string) => void;
    useCustomPrices: boolean;
    isCustomPricesDisabled: boolean;
    onCustomPricesChange: (checked: boolean) => void;
    hasCreatorRequestPermissions: boolean;
    creatorRequest: boolean;
    onCreatorRequestChange: (checked: boolean) => void;
    creatorRequestLockFilters: boolean;
    onCreatorRequestLockFiltersChange: (checked: boolean) => void;
    canEditOwnerSettings: boolean;
    notesBookerViewable: boolean;
    onNotesBookerViewableChange: (checked: boolean) => void;
    notesRequired: boolean;
    onNotesRequiredChange: (checked: boolean) => void;
    hidePublicFilters: boolean;
    onHidePublicFiltersChange: (checked: boolean) => void;
    hidePublicPrices: boolean;
    onHidePublicPricesChange: (checked: boolean) => void;
    currencySymbol: string;
    talentTotal?: number;
    cart: ListCartProps;
};

/** Right column: save/export, list settings, and the selected-creator cart. */
export function ListSettingsForm(props: ListSettingsFormProps) {
    const {
        onSubmit,
        sheetExportError,
        onDismissExportError,
        isCreatorRequestClosed,
        canExportList,
        isExportPending,
        onExport,
        name,
        isNameDisabled,
        onNameChange,
        useCustomPrices,
        isCustomPricesDisabled,
        onCustomPricesChange,
        hasCreatorRequestPermissions,
        creatorRequest,
        onCreatorRequestChange,
        creatorRequestLockFilters,
        onCreatorRequestLockFiltersChange,
        canEditOwnerSettings,
        notesBookerViewable,
        onNotesBookerViewableChange,
        notesRequired,
        onNotesRequiredChange,
        hidePublicFilters,
        onHidePublicFiltersChange,
        hidePublicPrices,
        onHidePublicPricesChange,
        currencySymbol,
        talentTotal,
        cart,
    } = props;

    return (
        <form className="bg-white p-4 lg:col-span-4" onSubmit={onSubmit}>
            {sheetExportError ? (
                <Alert className="mb-4" variant="destructive">
                    <AlertDescription>{sheetExportError}</AlertDescription>
                    <AlertAction>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Dismiss"
                            onClick={onDismissExportError}
                        >
                            <XIcon />
                        </Button>
                    </AlertAction>
                </Alert>
            ) : null}

            {/* Action buttons row */}
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <Button disabled={isCreatorRequestClosed} type="submit">
                    Save
                </Button>
                {canExportList ? (
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onExport}
                        disabled={isCreatorRequestClosed || isExportPending}
                    >
                        {isExportPending ? (
                            <Loader2 className="animate-spin" data-icon="inline-start" aria-label="Creating spreadsheet" />
                        ) : (
                            <FileSpreadsheetIcon data-icon="inline-start" aria-hidden />
                        )}
                        Export to Google Sheets
                    </Button>
                ) : null}
            </div>
            {canExportList ? (
                <p className="text-xs text-muted-foreground">
                    Creates a new Google Sheet in your account (via Grail&apos;s integration) with the list data shown here—including unsaved edits.
                </p>
            ) : null}

            {isCreatorRequestClosed ? (
                <p className="mt-4 text-destructive">
                    This creator request is currently closed. Only the list owner and admins can edit it.
                </p>
            ) : null}

            <FieldGroup className="mt-4 gap-6">
                <FormInput
                    label="List Name"
                    name="name"
                    id="name"
                    required
                    disabled={isNameDisabled}
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                />

                <FormCheckbox
                    id="customPrices"
                    name="customPrices"
                    label="Use Custom Prices"
                    description={
                        <>
                            Display custom prices to list viewers. Note: This will lock currency and campaign type <em>for bookers using this list</em>. Cannot be changed if there are already creators on the list.
                        </>
                    }
                    checked={useCustomPrices}
                    onChange={onCustomPricesChange}
                    disabled={isCustomPricesDisabled}
                />

                {hasCreatorRequestPermissions ? (
                    <FormCheckbox
                        id="creatorRequest"
                        name="creatorRequest"
                        label="List is Editable by all managers"
                        description="Allow other managers to edit this list."
                        checked={creatorRequest}
                        onChange={onCreatorRequestChange}
                    />
                ) : null}

                {hasCreatorRequestPermissions && creatorRequest ? (
                    <FormCheckbox
                        id="creatorRequestLockFilters"
                        name="creatorRequestLockFilters"
                        label="Filters Locked"
                        description="Prevent other managers from changing filters while adding creators."
                        checked={creatorRequestLockFilters}
                        onChange={onCreatorRequestLockFiltersChange}
                    />
                ) : null}

                {canEditOwnerSettings ? (
                    <FormCheckboxGroup label="Notes Settings">
                        <FormCheckbox
                            id="notesBookerViewable"
                            name="notesBookerViewable"
                            label="Allow Bookers to View Notes"
                            checked={notesBookerViewable}
                            onChange={onNotesBookerViewableChange}
                        />
                        <FormCheckbox
                            id="notesRequired"
                            name="notesRequired"
                            label="Require Notes"
                            checked={notesRequired}
                            onChange={onNotesRequiredChange}
                        />
                    </FormCheckboxGroup>
                ) : null}

                {canEditOwnerSettings ? (
                    <FormCheckboxGroup label="Public List Settings">
                        <FormCheckbox
                            id="hidePublicFilters"
                            name="hidePublicFilters"
                            label="Hide Filters on Public Share View"
                            checked={hidePublicFilters}
                            onChange={onHidePublicFiltersChange}
                        />
                        <FormCheckbox
                            id="hidePublicPrices"
                            name="hidePublicPrices"
                            label="Hide Prices on Public Share View"
                            checked={hidePublicPrices}
                            onChange={onHidePublicPricesChange}
                        />
                    </FormCheckboxGroup>
                ) : null}

                <div>
                    <div className="text-sm font-medium">
                        Talent <span className="text-destructive">*</span>
                        {talentTotal != null ? <> — {currencySymbol}{talentTotal.toLocaleString()}</> : null}
                    </div>
                </div>
            </FieldGroup>

            <ListCart {...cart} />
        </form>
    );
}
